<template>
  <v-sheet color="white" class="my-3 rounded-lg shadow">
    <v-container ref="bp_container" fluid fill-width class="ma-0 pa-0">
      <v-row class="ma-0 pa-0">
        <v-col cols="12" class="ma-0 pa-0">
          <div
            v-if="!outcomeVar"
            class="display-1 primary--text text--lighten-5 pt-5 mt-5"
            align="center"
          >
            NO VARIABLE SELECTED
          </div>

          <div
            v-else-if="!cohorts || cohorts.length == 0"
            class="display-1 primary--text text--lighten-5 pt-5 mt-5"
            align="center"
          >
            NO STUDY GROUPS SELECTED
          </div>

          <div v-else class="ttWrapper">
            <div
              ref="echartContainer"
              :style="
                'width: 100%; height: 100%; min-height: ' +
                  (maxCohorts * rowHeight + gridTop + gridBottom) +
                  'px;'
              "
              :class="{ fullScreenView: showFullPanelView }"
            ></div>

            <div class="tooltipButton">
              <span class="pr-2">Show:</span>

              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <span v-on="on">
                    <v-checkbox
                      v-model="showTooltips"
                      class="my-checkbox pa-0 ma-0 pt-2"
                      label="tooltips"
                      dense
                      hide-details
                    ></v-checkbox>
                  </span>
                </template>
                <span v-if="true">Show tooltips</span>
              </v-tooltip>

              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <span v-on="on">
                    <v-checkbox
                      v-model="showMeanValues"
                      class="my-checkbox pa-0 ma-0 pl-3"
                      label="mean values"
                      dense
                      hide-details
                    ></v-checkbox>
                  </span>
                </template>
                <span v-if="true">Show mean values</span>
              </v-tooltip>

              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <span v-on="on">
                    <v-checkbox
                      v-model="showOutliers"
                      class="my-checkbox pa-0 ma-0 pl-3"
                      label="outliers"
                      dense
                      hide-details
                    ></v-checkbox>
                  </span>
                </template>
                <span v-if="true">Show outliers</span>
              </v-tooltip>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>
    <canvas style="display: none;"> </canvas>
  </v-sheet>
</template>

<script>
import { mapState } from 'vuex';
import { state } from '@/store/modules/dataExplorer/types';
import { min, max, ascending, quantile, mean } from 'd3-array';
import { colors } from '@/utils/colors';
import { patterns } from '@/utils/patterns';
import * as echarts from 'echarts';

export default {
  components: {},
  props: {
    showTitleBar: {
      type: Boolean,
      required: false,
      default: true,
    },
    showFullPanelView: {
      type: Boolean,
      required: false,
      default: false,
    },
    analysis: {
      type: Object,
      required: true,
      default: () => {},
    },
    cohorts: {
      type: Array,
      required: true,
      default: () => [],
    },
    outcomeVar: {
      type: Object,
      required: true,
      default: null,
    },
    minHeight: {
      type: Number,
      required: false,
      default: 50,
    },
    minWidth: {
      type: Number,
      required: false,
      default: 300,
    },
    flipAxisNotNull: {
      type: Boolean,
      required: false,
      default: false,
    },
    doFlipAxis: {
      type: Boolean,
      required: false,
      default: false,
    },
    detailedView: {
      type: Object,
      required: false,
      default: null,
    },
    maxCohorts: {
      type: Number,
      required: false,
      default: 1,
    },
    rowHeight: {
      type: Number,
      required: false,
      default: 100,
    },
    titleClass: {
      type: String,
      required: false,
      default: '',
    },
    labelFontFamily: {
      type: String,
      required: false,
      default: 'sans-serif',
    },
    labelFontSize: {
      type: Number,
      required: false,
      default: 16,
    },
    maxYAxisLabelFrac: {
      type: Number,
      required: false,
      default: 0.45,
    },
    selectedAnalysisMode: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      bpkey: 1,
      resizeCount: 0,
      colors: colors,
      patterns: patterns,
      minValue: null,
      maxValue: null,
      firstVisit: null,
      lastVisit: null,
      bpPatterns: [],
      myChart: null,
      myChartContainerWidth: null,
      cohortData: null,
      boxPlotNumCohorts: 0,
      boxplotData: [],
      echartsData: [],
      option: {},
      showTooltips: false,
      showMeanValues: true,
      showOutliers: false,
      gridTop: 60,
      gridBottom: 100,
    };
  },
  computed: {
    ...mapState('dataExplorer', {
      collection: state.COLLECTION,
      data: state.DATA,
      outcomeVariables: state.OUTCOME_VARIABLES,
    }),
    axisFlippedNotNull() {
      return this.outcomeVar && this.outcomeVar.flip_axis != null;
    },
    axisFlipped() {
      return (
        this.outcomeVar &&
        this.outcomeVar.flip_axis &&
        this.analysis.input.comparisonField !== 'change'
      );
    },
  },
  watch: {
    cohorts() {
      var dom = this.$refs.echartContainer;
      this.$nextTick(() => {
        if (typeof dom == 'undefined') this.initializeGraph();
        this.updateVisits();
      });
    },
    outcomeVar() {
      this.updateVisits();
    },
    boxplotData() {
      this.makeGraph();
    },
    screenResize() {},
    showTooltips(st) {
      this.analysis.showTooltips = st;
      if (typeof this.option.tooltip != 'undefined') {
        this.option.tooltip.show = st;
        this.option.series[0].emphasis.disabled = !st;
        this.myChart.setOption(this.option, { silent: true });
      }
    },
    showMeanValues(smv) {
      this.analysis.showMeanValues = smv;
      if (typeof this.option.tooltip != 'undefined') {
        this.option.legend.selected.mean = smv;
        this.myChart.setOption(this.option, { silent: true });
      }
    },
    showOutliers(so) {
      this.analysis.showOutliers = so;
      if (typeof this.option.tooltip != 'undefined') {
        this.option.legend.selected.outliers = so;
        this.myChart.setOption(this.option, { silent: true });
      }
    },
    analysis: {
      handler(a) {
        if (a.showTooltips != this.showTooltips)
          this.showTooltips = a.showTooltips;
        if (a.showMeanValues != this.showMeanValues)
          this.showMeanValues = a.showMeanValues;
        if (a.showOutliers != this.showOutliers)
          this.showOutliers = a.showOutliers;
      },
      deep: true,
    },
  },
  mounted() {
    this.showTooltips = this.analysis.showTooltips;
    this.showMeanValues = this.analysis.showMeanValues;
    this.showOutliers = this.analysis.showOutliers;
    this.updateVisits();
    this.initializeGraph();
  },
  methods: {
    onResizeHandler(count) {
      if (count == this.resizeCount) {
        this.resizeCount = 0;
        this.$nextTick(() => {
          this.myChart.resize();
          this.updateStats();
        });
      }
    },
    onResize() {
      let rc = ++this.resizeCount;
      setTimeout(() => this.onResizeHandler(rc), 500);
    },
    // recompute grids based on current width
    getGrid() {
      // compute exact length of longest label (if possible)
      var maxTitleLengthPx = 0;
      const canvases = document.getElementsByTagName('canvas');

      if (canvases.length == 0) {
        maxTitleLengthPx = 200;
      } else {
        const ctx = canvases[0].getContext('2d');
        ctx.font = this.labelFontSize + 'px ' + this.labelFontFamily;
        this.boxplotData.forEach(d => {
          let mt = ctx.measureText(d.label);
          if (mt.width > maxTitleLengthPx) {
            maxTitleLengthPx = mt.width + 30;
          }
        });
      }

      // use at most maxYAxisLabelFrac of the available space for the y-axis captions
      if (this.myChartContainerWidth != null) {
        const maxTitleWidthPx =
          this.maxYAxisLabelFrac * this.myChartContainerWidth;
        if (maxTitleLengthPx > maxTitleWidthPx) {
          maxTitleLengthPx = maxTitleWidthPx;
        }
      }

      // flanking grids at zlevel 1 also hide unclipped graphics from central boxplot
      return [
        // middle grid - the graph itself
        {
          bottom: 100,
          left: maxTitleLengthPx,
          right: 80,
        },
        // left grid - y-axis label space
        {
          show: true,
          left: 0,
          top: this.gridTop,
          bottom: this.gridBottom,
          width: maxTitleLengthPx,
          borderWidth: 0,
          backgroundColor: 'white',
          zlevel: 1,
        },
        // right grid - y-axis scroll/zoom
        {
          show: true,
          width: 80,
          top: this.gridTop,
          bottom: this.gridBottom,
          right: 0,
          borderWidth: 0,
          backgroundColor: 'white',
          zlevel: 1,
        },
      ];
    },
    initializeGraph() {
      // -------------------------------------------------------------------------------------
      // handle custom color for each boxplot
      const BoxplotSeriesModel = echarts.ComponentModel.getClassesByMainType(
        'series'
      ).find(cls => cls.type === 'series.boxplot');
      const BoxplotSeriesModelFill = function(...args) {
        const _this = new BoxplotSeriesModel(...args);
        _this.visualDrawType = 'fill';
        return _this;
      };
      BoxplotSeriesModelFill.type = BoxplotSeriesModel.type;
      echarts.ComponentModel.registerClass(BoxplotSeriesModelFill);
      // -------------------------------------------------------------------------------------
      // Initialize the chart
      var dom = this.$refs.echartContainer;
      this.myChart = echarts.init(dom, null, {
        renderer: 'canvas',
        useDirtyRect: false,
      });

      const resizeObserver = new ResizeObserver(() => {
        this.myChartContainerWidth = dom.offsetWidth;
        this.option.grid = this.getGrid();
        this.myChart.setOption(this.option);
        this.myChart.resize();
      });

      this.myChartContainerWidth = dom.offsetWidth;

      resizeObserver.observe(dom);
    },
    // This function is called every time the data is updated
    makeGraph() {
      this.boxPlotNumCohorts = this.cohorts.length;

      // list of cohort titles/labels
      const cohortTitles = this.boxplotData.map(d => d.label);

      // maps cohort title/label to color
      const cohortColors = {};
      this.boxplotData.forEach(d => {
        cohortColors[d.label] = d.color;
      });

      let means = this.boxplotData.map(d => [
        d.label,
        Number(d.mean).toFixed(2),
      ]);
      means.unshift(['category', 'mean']);

      let bp = this;
      let bpTooltipFormatter = function(args) {
        let cd = bp.boxplotData[args.dataIndex];
        let html = '<b>' + cd.label + '</b><br>';
        ['min', 'q1', 'median', 'q3', 'max', 'mean'].forEach(lbl => {
          html = html + lbl + ': <b>' + Number(cd[lbl]).toFixed(2) + '</b><br>';
        });

        return html;
      };

      let outlierTooltipFormatter = function(args) {
        const label_re = /^.*\|\s*([^|]+)$/;
        let m = args.value[0].match(label_re);
        let cohortLbl = m ? m[1] : args.value[0];
        let value = args.value[1];
        let subjs = bp.getSubjectsWithValue(cohortLbl, value);
        let html = '<b>outlier; value=' + value + '</b><br>';
        html += 'subject id(s):<br>';
        subjs.forEach(s => {
          html += s.subject_num + '<br>';
        });
        return html;
      };

      // -------------------------------------------------------------------------------------
      // Graph settings
      this.option = {
        animation: true,
        title: [
          {
            text:
              bp.outcomeVar.abbreviation === bp.outcomeVar.label
                ? bp.outcomeVar.abbreviation
                : bp.outcomeVar.abbreviation + ' | ' + bp.outcomeVar.label,
            left: 'center',
            show: bp.showTitleBar,
          },
        ],
        textStyle: {
          fontSize: bp.labelFontSize,
          fontFamily: bp.labelFontFamily,
          color: '#000000',
        },
        legend: {
          selected: {
            mean: bp.showMeanValues,
            outliers: bp.showOutliers,
          },
          top: 30,
          left: 'center',
          data: [{ name: 'mean' }, { name: 'outliers' }],
          // **********************************************
          // This is why the legend for boxplot has a much thicker border than the rest of the legends
          // I attampted to fix this, which the following line of code works to a certain extent
          // However, once you click the icon and the legend is deselected, the border will go back to normal
          // The function that controls the change in icon is called handleLegendSelectChanged()
          // itemStyle:{borderWidth:0},
          // **********************************************
          borderWidth: 1,
          lineStyle: {
            color: '#000',
            width: 0.5,
          },
        },
        dataset: [
          // DatasetIndex0
          {
            id: 'raw',
            source: bp.echartsData,
          },
          // DatasetIndex1
          {
            transform: {
              type: 'boxplot',
              //              print: true,
              config: {
                itemNameFormatter: function(params) {
                  return cohortTitles[params.value];
                },
              },
            },
          },
          // DatasetIndex2
          {
            fromDatasetIndex: 1,
            fromTransformResult: 1,
          },
          // DatasetIndex3
          {
            id: 'mean',
            fromDatasetIndex: 1,
            source: means,
          },
        ],
        dataZoom: [
          {
            id: 'dataZoomX',
            type: 'slider',
            xAxisIndex: [0],
            height: 25,
            bottom: 10,
            filterMode: 'none', // this allows the box to go out of frame
            showDataShadow: false,
          },
          {
            // show:false,
            id: 'dataZoomY',
            type: 'slider',
            yAxisIndex: [0],
            filterMode: 'filter', // this prevents all the dots from showing up on the same line when there is only 1 boxplot
            right: 30,
            width: 25,
            showDataShadow: false,
            zlevel: 1,
            textStyle: {
              color: '#000',
            },
            labelFormatter: function() {
              return ' ';
            },
            dataBackground: {
              areaStyle: {
                color: '#333',
              },
            },
          },
        ],
        tooltip: {
          trigger: 'item',
          axisPointer: { type: 'shadow' },
          show: bp.showTooltips,
        }, // this controls the hover-effects
        yAxis: {
          type: 'category',
          splitLine: { show: true },
          nameGap: 0,
          splitArea: { show: true },
          zlevel: 1,
          inverse: true,
          axisLabel: {
            fontSize: bp.labelFontSize,
            fontFamily: bp.labelFontFamily,
            overflow: 'truncate',
            align: 'right',
          },
        },
        xAxis: {
          show: true,
          type: 'value',
          splitArea: { show: true },
          max: function(value) {
            return Math.ceil(value.max + 0.1 * (value.max - value.min));
          },
          min: function(value) {
            return Math.floor(value.min - 0.1 * (value.max - value.min));
          },
          name: this.axisFlippedNotNull
            ? 'Better <----> Worse'
            : this.outcomeVar
            ? this.outcomeVar.label
            : '',
          nameLocation: 'middle',
          nameGap: 35,
          scale: true,
          axisLabel: {
            fontSize: bp.labelFontSize,
            fontFamily: bp.labelFontFamily,
            overflow: 'truncate',
          },
          axisPointer: {
            show: true,
            type: 'line',
            triggerTooltip: false,
            lineStyle: {
              color: '#cc0066',
              width: 2,
            },
            zlevel: 1,
          },
          inverse: bp.axisFlipped,
        },
        grid: bp.getGrid(),
        series: [
          {
            name: 'boxplot',
            type: 'boxplot',
            datasetIndex: 1,
            itemStyle: {
              color: function(seriesIndex) {
                return cohortColors[seriesIndex.name];
              },
              borderColor: '#000',
              borderWidth: 1.5,
            },
            tooltip: {
              trigger: 'item',
              axisPointer: {
                type: 'cross',
              },
              formatter: bpTooltipFormatter,
            },
            z: 1,
            emphasis: {
              focus: 'self',
              blurScope: 'coordinateSystem',
              disabled: !bp.showTooltips,
            },
          },
          {
            name: 'outliers',
            type: 'scatter',
            datasetIndex: 2,
            z: 2,
            itemStyle: {
              color: '#ff0000',
            },
            tooltip: {
              trigger: 'item',
              axisPointer: {
                type: 'cross',
              },
              formatter: outlierTooltipFormatter,
            },
          },
          {
            name: 'mean',
            type: 'scatter',
            encode: { x: 1, y: 0 },
            datasetIndex: 3,
            z: 2,
            itemStyle: {
              color: '#000000',
            },
            symbolSize: 12,
          },
        ],
        responsive: true,
        maintainAspectRatio: true,
      };

      if (this.option && typeof this.option === 'object') {
        this.myChart.setOption(this.option, true);
      }

      this.myChart.on('legendselectchanged', params => {
        this.handleLegendSelectChanged(params);
      });
    },

    handleLegendSelectChanged(params) {
      let cval = this.option.legend.selected[params.name];
      let newval = !cval;
      if (params.name == 'mean') {
        this.showMeanValues = newval;
      } else if (params.name == 'outliers') {
        this.showOutliers = newval;
      }
    },

    updateVisits() {
      var bp = this;
      if (this.outcomeVar == null) return;
      this.collection.observation_variables_list.forEach(v => {
        if (v.ontology.id == bp.outcomeVar.id) {
          if (v.first_visit_event != null) {
            bp.firstVisit = v.first_visit_event;
            bp.lastVisit = v.last_visit_event;
          } else if (v.first_visit_num != null) {
            bp.firstVisit = v.first_visit_num;
            bp.lastVisit = v.last_visit_num;
          }
        }
      });
      this.$nextTick(() => {
        this.updateStats();
      });
    },
    dataAccFn() {
      var comparisonMeasure = this.analysis.input.comparisonField;
      let accFn = null;

      if (this.outcomeVar.type == 'observation') {
        if (this.outcomeVar.is_longitudinal) {
          if (comparisonMeasure == 'change') {
            accFn = x =>
              x[this.outcomeVar.id]['lastVisit'] -
              x[this.outcomeVar.id]['firstVisit'];
          } else {
            accFn = x => x[this.outcomeVar.id][comparisonMeasure];
          }
        }
        // cross-sectional
        else {
          accFn = x => x[this.outcomeVar.id].value;
        }
      } else if (
        this.selectedAnalysisMode == 'Outcome based' &&
        this.outcomeVar.type == 'subject'
      ) {
        accFn = x => x[this.outcomeVar.label];
      }
      return accFn;
    },
    getSubjectsWithValue(cohortLabel, value) {
      let accFn = this.dataAccFn();
      let subjects = [];
      let cohort = null;
      this.cohorts.forEach(c => {
        let cl = c.label;
        let m = cl.match(/^\[O\]_(.*$)/);
        if (m) cl = m[1];
        if (cl == cohortLabel) cohort = c;
      });
      if (cohort != null) {
        const subjids = [];
        cohort.subject_ids.forEach(sid => {
          subjids[sid] = 1;
        });
        this.data.forEach(d => {
          if (d.subject_id in subjids) {
            if (accFn(d) == value) {
              subjects.push(d);
            }
          }
        });
      }
      return subjects;
    },
    updateStats_aux(visit, label_prefix, data) {
      // compute boxplot stats for each cohort
      this.cohorts.forEach(c => {
        const subjids = [];
        c.subject_ids.forEach(sid => {
          subjids[sid] = 1;
        });
        let cohortData = null;
        if (this.outcomeVar.type == 'observation') {
          if (visit == 'change') {
            cohortData = this.data
              .filter(d => d.subject_id in subjids)
              .map(
                x =>
                  x[this.outcomeVar.id]['lastVisit'] -
                  x[this.outcomeVar.id]['firstVisit']
              )
              .sort(ascending);
          } else {
            cohortData = this.data
              .filter(d => d.subject_id in subjids)
              .map(x => x[this.outcomeVar.id][visit])
              .sort(ascending);
          }
        } else if (
          this.selectedAnalysisMode == 'Outcome based' &&
          this.outcomeVar.type == 'subject'
        ) {
          cohortData = this.data
            .filter(d => d.subject_id in subjids)
            .map(x => x[this.outcomeVar.label])
            .sort(ascending);
        } else {
          return;
        }

        const cmin = min(cohortData);
        const cmax = max(cohortData);
        var q1 = quantile(cohortData, 0.25);
        var median = quantile(cohortData, 0.5);
        var q3 = quantile(cohortData, 0.75);
        var iqr = q3 - q1;

        data.push({
          data: cohortData,
          color: c.color,
          pattern: c.pattern,
          label: label_prefix + c.label.replace(/^\[O\]_/, ''),
          cohort: c,
          min: cmin,
          max: cmax,
          mean: mean(cohortData),
          'q1 - 1.5 * iqr': q1 - 1.5 * iqr,
          q1: q1,
          median: median,
          q3: q3,
          'q3 + 1.5 * iqr': q3 + 1.5 * iqr,
        });
      });
    },

    updateStats() {
      if (!this.outcomeVar) return;

      this.boxplotData = [];
      var bpData = [];

      if (this.outcomeVar.type == 'observation') {
        // overall min, max value
        var accFn = x => x[this.outcomeVar.id];
        var allData = this.data.map(x => accFn(x));
        var comparisonMeasure = this.analysis.input.comparisonField;

        if (this.outcomeVar.is_longitudinal) {
          if (
            comparisonMeasure === 'firstVisit' ||
            comparisonMeasure === 'lastVisit'
          ) {
            const firstMin = min(allData, d => d.firstVisit);
            const lastMin = min(allData, d => d.lastVisit);
            this.minValue = Math.min(firstMin, lastMin);
            const firstMax = max(allData, d => d.firstVisit);
            const lastMax = max(allData, d => d.lastVisit);
            this.maxValue = Math.max(firstMax, lastMax);
          } else if (comparisonMeasure === 'change') {
            this.minValue = min(allData, d => d.lastVisit - d.firstVisit);
            this.maxValue = max(allData, d => d.lastVisit - d.firstVisit);
          }
        } else {
          this.minValue = min(allData, d => d.value);
          this.maxValue = max(allData, d => d.value);
        }

        if (this.outcomeVar.is_longitudinal) {
          if (comparisonMeasure === 'firstVisit') {
            this.updateStats_aux('firstVisit', this.firstVisit + ' | ', bpData);
          } else if (comparisonMeasure === 'lastVisit') {
            this.updateStats_aux('lastVisit', this.lastVisit + ' | ', bpData);
          } else if (comparisonMeasure === 'change') {
            this.updateStats_aux('change', 'change' + ' | ', bpData);
          }
        }
        // cross-sectional data
        else {
          this.updateStats_aux('value', '', bpData);
        }
      } else if (
        this.selectedAnalysisMode == 'Outcome based' &&
        this.outcomeVar.type == 'subject'
      ) {
        // overall min, max value
        var subjAccFn = x => x[this.outcomeVar.label];
        var subjAllData = this.data.map(x => subjAccFn(x));

        this.minValue = min(subjAllData);
        this.maxValue = max(subjAllData);

        this.updateStats_aux('', '', bpData);
      }

      this.boxplotData = bpData;
      this.echartsData = bpData.map(d => d.data);
    },
  },
};
</script>

<style lang="scss" scoped>
.ttWrapper {
  position: relative;
}

.tooltipButton {
  position: absolute;
  top: 0rem;
  left: 1rem;
  margin: auto;
}

.fullScreenView {
  left: 0% !important;
  margin-top: 2rem;
}

.my-checkbox {
  display: inline-block;
}

::v-deep .my-checkbox .v-label {
  font-size: 15px;
}
</style>
